import React, { useEffect, useId, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import MoneyInput from '../../common/MoneyInput';
import LegacyIndividualAffordabilityFields, { legacyIndividualAffordabilityGroups } from './LegacyIndividualAffordabilityFields';
import OtherLivingCostFields from './OtherLivingCostFields';
import { capturedAffordability, hasIndividualBreakdown, individualBreakdownGroups, individualBreakdownTotals,
  otherLivingExpenseTotal, sumMonthlyAmounts } from '../../../services/individualAffordability';
import './IndividualAffordabilityFields.css';

const money = (value?: number) => value == null ? 'Not captured' : `R ${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export default function IndividualAffordabilityFields({ includeIncome = true }: { includeIncome?: boolean } = {}) {
  const { control, setValue, getValues, formState: { errors } } = useFormContext();
  const financial = useWatch({ control, name: 'financial' });
  const net = useWatch({ control, name: 'individual.monthlyNetIncome' });
  const [editRecorded, setEditRecorded] = useState(false);
  const [captureBreakdown, setCaptureBreakdown] = useState(false);
  const recordedHelpId = useId();
  const detailed = hasIndividualBreakdown(financial), totals = individualBreakdownTotals(financial);
  const figures = capturedAffordability({ financial, individual: { monthlyNetIncome: net } });
  const readRecorded = () => {
    const f = getValues('financial');
    const hasRecorded = f?.affordabilityCaptured || f?.livingExpenses != null || f?.income?.grossIncome > 0
      || f?.income?.netIncome > 0 || getValues('individual.monthlyNetIncome') > 0;
    return hasIndividualBreakdown(f) || !hasRecorded ? undefined : { ...f, income: { ...f?.income },
      expenses: { ...f?.expenses }, household: { ...f?.household },
      netIncome: getValues('individual.monthlyNetIncome') ?? f?.income?.netIncome };
  };
  const [recorded, setRecorded] = useState(readRecorded);
  const usingRecorded = !!recorded && !detailed;
  const breakdownLocked = usingRecorded && !captureBreakdown;
  // Returning-customer capture initialises gross to zero even when only take-home pay was saved.
  const recordedGross = recorded?.income?.grossIncome === 0 && recorded?.netIncome > 0
    ? undefined : recorded?.income?.grossIncome;
  const otherTotal = otherLivingExpenseTotal(financial);
  const itemised = financial?.otherLivingExpenseItems != null;
  const expenses = sumMonthlyAmounts([figures.livingExpenses, figures.maintenance, figures.debtPayments]);
  const totalIncome = sumMonthlyAmounts([figures.netIncome, figures.otherIncome]);
  const remaining = totalIncome == null || expenses == null ? undefined : Math.round((totalIncome - expenses) * 100) / 100;
  const invalidObligations = (detailed || !!recorded) && (figures.maintenance == null || figures.debtPayments == null);
  useEffect(() => {
    if (editRecorded || (!includeIncome && !detailed)) return;
    if ((!detailed && recorded) || (detailed && totals.grossIncome != null && totals.netIncome != null && totals.necessaryExpenses != null))
      setValue('financial.affordabilityCaptured', true);
    if (detailed && totals.grossIncome != null && totals.netIncome != null) {
      setValue('financial.income.grossIncome', totals.grossIncome);
      setValue('financial.income.netIncome', totals.netIncome);
      setValue('financial.income.otherIncome', 0);
      setValue('individual.monthlyNetIncome', totals.netIncome);
    }
    if (!detailed && net != null) setValue('financial.income.netIncome', net);
    if (figures.livingExpenses != null) {
      setValue('financial.livingExpenses', figures.livingExpenses);
      setValue('individual.monthlyLivingExpenses', figures.livingExpenses);
    }
    if (itemised) setValue('financial.otherLivingExpenses', otherTotal);
    if (figures.debtPayments != null) setValue('financial.hasExistingCreditObligations', figures.debtPayments > 0);
  }, [editRecorded, includeIncome, detailed, recorded, net, totals.grossIncome, totals.netIncome, totals.necessaryExpenses, figures.livingExpenses, figures.debtPayments, otherTotal, itemised, setValue]);
  const field = (name: string, label: string, id: string) => {
    const error = name.split('.').reduce<any>((item, key) => item?.[key], errors);
    return <div className="individual-money-row" key={name}>
      <label htmlFor={id}>{label}</label>
      <Controller name={name} control={control} rules={{ min: { value: 0, message: 'Enter a non-negative amount.' } }}
        render={({ field: input }) => <MoneyInput id={id} name={input.name} ref={input.ref} value={input.value} blankAsZero
          onValueChange={input.onChange} onBlur={input.onBlur} min={0} className={`form-control ${error ? 'is-invalid' : ''}`}
          aria-invalid={!!error} aria-describedby="individual-money-help" />} />
      {error && <span role="alert" className="text-danger small">{String(error.message)}</span>}
    </div>;
  };
  const group = (index: number) => {
    const section = individualBreakdownGroups[index];
    const total = !detailed ? undefined : index === 0 ? totals.grossIncome : index === 1 ? totals.deductions : figures.livingExpenses;
    return <fieldset className="mb-4" aria-label={section.title} disabled={breakdownLocked}>
      <legend className="h6 mb-3">{section.title}</legend>
      {section.fields.map(item => field(`financial.individualBreakdown.${item.name}`, item.label, `individual-breakdown-${item.name}`))}
      {index === 2 && <OtherLivingCostFields />}
      <div className="d-flex justify-content-between gap-3 border-top pt-3 fw-semibold">
        <span>Total {section.title}</span><span>{money(total)}</span>
      </div>
    </fieldset>;
  };
  const obligations = <fieldset className="mb-4" aria-label="Maintenance and declared repayments">
    <legend className="h6 mb-3">Credit obligations · Maintenance and declared repayments</legend>
    {legacyIndividualAffordabilityGroups[1].fields.slice(8).map(item =>
      field(item.name, `${item.label} (R)`, `obligation-${item.name}`))}
    {invalidObligations && <p role="alert" className="text-danger">Check maintenance and declared repayments under Credit obligations.</p>}
  </fieldset>;
  if (editRecorded || (!includeIncome && !detailed)) return <>
    {includeIncome && <button type="button" className="btn btn-link mb-3" onClick={() => {
      setRecorded(readRecorded());
      setEditRecorded(false);
    }}>Back to income and expense breakdown</button>}
    <LegacyIndividualAffordabilityFields includeIncome={includeIncome} />
  </>;
  return <section className="form-section individual-financial-breakdown" aria-labelledby="individual-affordability-heading">
    <h6 id="individual-affordability-heading" className="form-section-title">Income and Expenses</h6>
    <p id="individual-money-help" className="small text-muted">Amounts in rand per month. Leave unused categories blank; they count as R0.
      Include each cost once; keep maintenance and loan repayments under Credit obligations.</p>
    {recorded && <div className="alert alert-info mb-4" role="note" aria-labelledby="recorded-financial-heading">
      <div className="d-flex align-items-center gap-2 mb-2">
        <h6 id="recorded-financial-heading" className="mb-0">Previously recorded information</h6>
        <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={<Popover id={recordedHelpId}>
          <Popover.Header as="h3">About previously recorded information</Popover.Header>
          <Popover.Body>
            <p>This customer has saved totals without a category breakdown.
              {usingRecorded ? ' These saved figures are currently in use; the blank categories below have not been captured.'
                : ' The current breakdown replaces these saved totals; they are not added together.'}</p>
            {usingRecorded && <p className="mb-0">To replace these totals, capture all current income, payslip deductions and household costs.
              Entering the first category replaces the saved totals; gross salary cannot be inferred from take-home income.</p>}
          </Popover.Body>
        </Popover>}><button type="button" className="btn btn-link p-0" aria-label="About previously recorded information">ⓘ</button></OverlayTrigger>
      </div>
      <p className="small mb-2">{usingRecorded ? 'Saved totals currently in use.' : 'Replaced by the current breakdown.'}</p>
      <dl className="row mb-2"><dt className="col-8">Recorded gross income</dt><dd className="col-4">{money(recordedGross)}</dd>
        <dt className="col-8">Recorded take-home income</dt><dd className="col-4">{money(recorded.netIncome)}</dd>
        <dt className="col-8">Recorded other net income</dt><dd className="col-4">{money(recorded.income?.otherIncome)}</dd></dl>
      {usingRecorded && <>
        <div className="d-flex gap-2 flex-wrap">
          {breakdownLocked && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setCaptureBreakdown(true)}>Capture current breakdown</button>}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditRecorded(true)}>Edit recorded information</button>
        </div>
      </>}
      <details className="mt-2"><summary>Recorded household costs</summary><dl className="row mt-2">
        {legacyIndividualAffordabilityGroups[1].fields.slice(0, 8).map(item => {
          const value = item.name.split('.').slice(1).reduce<any>((previous, key) => previous?.[key], recorded);
          return <React.Fragment key={item.name}><dt className="col-8">{item.label}</dt><dd className="col-4">{money(value)}</dd></React.Fragment>;
        })}</dl></details>
    </div>}
    <div className="row g-4">
      {includeIncome && <section className="col-12 col-lg-6" aria-label="Income and credit obligations">
        {group(0)}{group(1)}
        <div className="d-flex justify-content-between gap-3 border-top pt-3 mb-4 fw-semibold">
          <span>{usingRecorded ? 'Recorded take-home income in use' : 'Total Net Income'}</span><span>{money(figures.netIncome)}</span>
        </div>
        {obligations}
      </section>}
      <div className={includeIncome ? 'col-12 col-lg-6' : 'col-12'}>{group(2)}</div>
    </div>
    {detailed && (totals.grossIncome == null || totals.netIncome == null || totals.necessaryExpenses == null) &&
      <p role="alert" className="text-danger">Enter non-negative amounts to cents. Deductions cannot exceed gross income.</p>}
    {!includeIncome && obligations}
    <div className="row g-3 border-top pt-3" aria-live="polite">
      <div className="col-md-4"><div className="small text-muted">{usingRecorded ? 'Recorded take-home income in use' : 'Net Income'}</div><strong>{money(figures.netIncome)}</strong></div>
      <div className="col-md-4"><div className="small text-muted">Total monthly expenses</div><strong>{money(expenses)}</strong></div>
      <div className="col-md-4"><div className="small text-muted">Income left before the new loan</div><strong className={remaining != null && remaining < 0 ? 'text-danger' : ''}>{money(remaining)}</strong></div>
    </div>
  </section>;
}
